"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const experiences = [{
  number: "01",
  title: "AI Threat Detection",
  description: "Our advanced AI engines monitor your network traffic in real-time, identifying anomalies and potential threats before they escalate. By leveraging machine learning, we stay ahead of evolving cyber risks, ensuring your digital infrastructure remains resilient against sophisticated attacks and unauthorized access attempts.",
  linkLabel: "View details",
  href: "/ExpertisePage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/ai_threat_detection.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/ai_threat_detection.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/ai_threat_detection.webp-wvc-sizes",
    alt: "AI-powered network security monitoring dashboard"
  },
  layout: "image-left"
}, {
  number: "02",
  title: "24/7 Security Monitoring",
  description: "Cyber threats never sleep, and neither do we. Our dedicated security operations center provides continuous, round-the-clock surveillance of your systems. We provide immediate alerts and rapid intervention, ensuring that any suspicious activity is neutralized instantly to protect your business continuity and data integrity.",
  linkLabel: "View details",
  href: "/ExpertisePage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/security_monitoring.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/security_monitoring.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/security_monitoring.webp-wvc-sizes",
    alt: "Security operations center monitoring digital threats"
  },
  layout: "image-right",
  onLinen: true
}, {
  number: "03",
  title: "Phishing Prevention",
  description: "Human error remains the leading cause of security breaches. We implement robust email filtering and verification protocols to block malicious phishing attempts. Our proactive approach ensures that your employees are shielded from deceptive communications designed to steal credentials or deploy ransomware.",
  linkLabel: "View details",
  href: "/ExpertisePage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/phishing_prevention.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/phishing_prevention.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717971/img/phishing_prevention.png-wvc-sizes",
    alt: "Email security interface blocking phishing attempts"
  },
  layout: "image-left-wide"
}];
function RevealText({
  text,
  className,
  delayStep = 30
}: {
  text: string;
  className?: string;
  delayStep?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.15
  });
  const words = text.split(" ");
  return <p ref={ref} className={className}>
      {words.map((word, i) => <span key={i} data-index={i} className="inline-block transition-all duration-700 ease-out" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(8px)",
      transitionDelay: `${i * delayStep}ms`
    }}>
          {word}
          {i < words.length - 1 ? "\u00A0" : ""}
        </span>)}
    </p>;
}
function ExperienceBlock({
  item,
  index
}: {
  item: (typeof experiences)[number];
  index: number;
}) {
  const {
    ref: numberRef,
    isVisible: numberVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const isImageLeft = item.layout === "image-left" || item.layout === "image-left-wide";
  const isWide = item.layout === "image-left-wide";
  return <div data-index={index} className={`relative ${item.onLinen ? "py-20 lg:py-28" : ""}`}>
      {item.onLinen && <div aria-hidden="true" className="absolute inset-x-0 inset-y-0 -mx-6 sm:-mx-10 lg:-mx-16 bg-blue-50" />}

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {}
        <div ref={imageRef} className={`overflow-hidden rounded-lg ${isImageLeft ? isWide ? "lg:col-start-1 lg:col-end-8 lg:row-start-1" : "lg:col-start-1 lg:col-end-7 lg:row-start-1" : "lg:col-start-7 lg:col-end-13 lg:row-start-1"}`} style={{
        opacity: imageVisible ? 1 : 0,
        transform: imageVisible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1), transform 1000ms cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          <div className="overflow-hidden rounded-lg group shadow-md">
            <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="w-full h-[420px] sm:h-[520px] lg:h-[600px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
          </div>
        </div>

        {}
        <div className={`${isImageLeft ? isWide ? "lg:col-start-9 lg:col-end-12 lg:row-start-1" : "lg:col-start-8 lg:col-end-12 lg:row-start-1" : "lg:col-start-2 lg:col-end-6 lg:row-start-1"}`}>
          <div ref={numberRef} className="font-default text-[80px] leading-none text-blue-200 mb-6" style={{
          opacity: numberVisible ? 1 : 0,
          transform: numberVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 1200ms cubic-bezier(0.22, 1, 0.36, 1), transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            {item.number}
          </div>

          <h3 ref={headingRef} className="font-default text-[40px] leading-[1.1] tracking-tight text-slate-900 mb-6" style={{
          opacity: headingVisible ? 1 : 0,
          transform: headingVisible ? "translateY(0)" : "translateY(16px)",
          transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 150ms, transform 900ms cubic-bezier(0.22, 1, 0.36, 1) 150ms"
        }}>
            {item.title}
          </h3>

          <RevealText text={item.description} className="font-default text-[18px] leading-[1.7] text-slate-700 mb-8" />

          <Link to={item.href} className="group inline-flex items-center gap-2 text-sm tracking-widest uppercase text-blue-600 font-semibold">
            <span className="relative">
              {item.linkLabel}
              <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-full bg-blue-600 origin-left scale-x-100 transition-transform duration-500 ease-out group-hover:scale-x-0" />
              <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-full bg-blue-600 origin-right scale-x-0 transition-transform duration-500 ease-out delay-150 group-hover:scale-x-100" />
            </span>
            <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>;
}
export default function Servicedetail() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  return <section data-section-id="4291" id="servicedetail" className="bg-slate-50 text-slate-900 py-24 sm:py-32 lg:py-40 px-6 sm:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="text-center mb-20 lg:mb-28">
          <div ref={eyebrowRef} className="font-default text-xs tracking-[0.3em] uppercase text-blue-600 mb-8 font-bold" style={{
          opacity: eyebrowVisible ? 1 : 0,
          transform: eyebrowVisible ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 800ms cubic-bezier(0.22, 1, 0.36, 1), transform 800ms cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            &mdash; Our Core Services
          </div>

          <h2 ref={titleRef} className="font-default text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight max-w-4xl mx-auto text-slate-900" style={{
          opacity: titleVisible ? 1 : 0,
          transform: titleVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1) 200ms, transform 1000ms cubic-bezier(0.22, 1, 0.36, 1) 200ms"
        }}>
            Advanced protection for
            <br />
            your <em className="italic font-light text-blue-600">digital future.</em>
          </h2>
        </div>

        {}
        <div className="flex flex-col gap-32 lg:gap-40">
          {experiences.map((item, i) => <ExperienceBlock key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}